import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';

import { DsLoggingService } from '@cubigo/digital-signage';

import { TranslateModule } from '@ngx-translate/core';
import { finalize, from, switchMap, take } from 'rxjs';

import { LocalStorageKey } from 'app/shared/enums';
import { AuthService } from 'app/shared/services';
import { DataCenterDataService } from 'app/shared/services/data';
import { emailValidator } from 'app/shared/validators/custom-email.validator';

@Component({
  selector: 'app-login',
  imports: [ReactiveFormsModule, TranslateModule],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LoginComponent {
  private readonly authService = inject(AuthService);
  private readonly dataCenterDataService = inject(DataCenterDataService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly logging = inject(DsLoggingService);
  private readonly router = inject(Router);

  public readonly loginFailed = signal(false);
  public readonly isLoading = signal(false);
  public readonly emailControl = new FormControl(localStorage.getItem(LocalStorageKey.LoginHint) ?? '', {
    nonNullable: true, validators: [Validators.required, emailValidator()]
  });
  public readonly passwordControl = new FormControl('', { nonNullable: true, validators: [Validators.required] });
  public readonly loginForm = new FormGroup({ email: this.emailControl, password: this.passwordControl });
  private readonly formStatus = toSignal(this.loginForm.statusChanges, { initialValue: this.loginForm.status });
  public readonly submitDisabled = computed(() => this.formStatus() !== 'VALID' || this.isLoading());

  public onKey(): void {
    this.loginFailed.set(false);
  }

  public onSubmit(): void {
    if (this.loginForm.invalid || this.isLoading()) {
      return;
    }

    const { email, password } = this.loginForm.getRawValue();
    this.isLoading.set(true);
    this.loginFailed.set(false);
    this.dataCenterDataService.getDataCenterRegionOfUser(email).pipe(
      take(1),
      switchMap(dataCenter => {
        this.logging.setInstrumentationKeyOnRegion(dataCenter.region);
        localStorage.setItem(LocalStorageKey.DataCenterPrefix, dataCenter.region);
        localStorage.setItem(LocalStorageKey.LoginHint, email);

        return this.authService.login(email, password).pipe(take(1));
      }),
      switchMap(() => from(this.router.navigate(['']))),
      takeUntilDestroyed(this.destroyRef),
      finalize(() => this.isLoading.set(false))
    ).subscribe({
      error: () => this.loginFailed.set(true)
    });
  }
}
